// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * Defers `setup` past synchronous module loading via `queueMicrotask`.
 *
 * Use this for MobX `reaction` / `autorun` / `when` calls in singleton
 * constructors that read **other** singletons in their data function.
 *
 * Singletons created with `export default new Foo()` participate in their
 * file's top-level execution. When two singletons import each other (directly
 * or via a chain), one of them will see `undefined` for the other while ES
 * modules are still resolving. MobX's initial dependency-tracking call runs
 * the data function synchronously during reaction setup — if that read hits
 * an undefined singleton, the reaction throws "Cannot read properties of
 * undefined". A microtask is enough to push the setup past the synchronous
 * module-load chain so all default exports are resolved.
 *
 * Methods are fine to call directly in constructors (the binding resolves at
 * call time, not at setup time). Only `reaction`, `autorun`, `when`, and
 * other code that synchronously reads cross-store observables needs this.
 */
export function deferUntilModulesLoaded(setup: () => void): void {
	queueMicrotask(setup);
}
